import { formatPercentage } from '@/domain/common/format'
import { Typography } from '@/ui/atoms/typography/Typography'
import { MobileViewOptions } from '@/ui/molecules/data-table/types'
import { PointsBadge } from '@/ui/molecules/points-badge/PointsBadge'

import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { Percentage } from '@/domain/types/NumericValues'
import { IsolatedPartnerPointsPerPair } from '@/config/chain/types'
import { cn } from '@/ui/utils/style'
import { PointsObject } from '@/domain/pointsApi/types'
import { Warn } from '@/ui/molecules/warn/Warn'
import { Skeleton } from '@/ui/atoms/skeleton/Skeleton'

type BaseProps = {
  apy?: Percentage
  points?: PointsObject
  baseAPYDisplay?: string
  totalAPYDisplay?: string
  mobileViewOptions?: MobileViewOptions
  pointsError: boolean
  pointsLoading: boolean
  className?: string
  'data-testid'?: string
  partnerPoints?: IsolatedPartnerPointsPerPair[]
  extraIncentives?: string
  additionalAPY?: string
  showAdditionalAPYInCell?: boolean
  gradient?: boolean
  cellColorOverride?: string
  stacked?: boolean
  inline?: boolean
}

type NonLpProps = BaseProps & {
  action: 'deposit' | 'borrow' | 'lend'
  asset: TokenSymbol
  poolName?: never
}

type LpProps = BaseProps & {
  action: 'lp-deposit'
  poolName: string
  asset?: never
}

type CellContentProps = NonLpProps | LpProps

export function ApyWithRewardsCell({
  mobileViewOptions,
  className,
  ...rest
}: CellContentProps & { mobileViewOptions?: MobileViewOptions }) {
  if (mobileViewOptions?.isMobileView) {
    return (
      <div
        className={cn(
          'flex flex-row items-center',
          mobileViewOptions.showOnMobile ? 'justify-center' : 'justify-between',
        )}
      >
        {!mobileViewOptions.showOnMobile && <Typography variant="prompt">{mobileViewOptions.rowTitle}</Typography>}
        <CellContent {...rest} className={className} />
      </div>
    )
  }

  return <CellContent {...rest} className={className} />
}

function CellContent(props: CellContentProps) {
  const {
    apy,
    points,
    className,
    partnerPoints,
    extraIncentives,
    additionalAPY,
    baseAPYDisplay,
    totalAPYDisplay,
    showAdditionalAPYInCell = true,
    gradient = false,
    cellColorOverride,
    stacked = false,
    inline = false,
    pointsError,
    pointsLoading,
  } = props
  const isPointsLoading = pointsLoading && !!apy
  const isPointsError = !isPointsLoading && pointsError && !!apy
  const hasPointsBadge = !!points?.multiplier || !!partnerPoints?.length
  const showRewardsRow = isPointsLoading || hasPointsBadge || isPointsError

  return (
    <div className={cn('flex justify-end', className)}>
      <div className={cn('flex flex-col items-end justify-end gap-1', inline && 'flex-row items-center gap-2')}>
        <div className="flex justify-end">
          <div className={cn(stacked && 'mb-0.5 flex flex-col items-end')}>
            <Typography element="span" gradient={gradient} className={cn('text-sm', gradient && 'font-bold')}>
              {formatPercentage(apy)}
            </Typography>
            {additionalAPY && showAdditionalAPYInCell && (
              <span className={cn('ml-0.5 text-xs', cellColorOverride)}>
                ({additionalAPY.startsWith('-') ? '' : '+'}
                {additionalAPY})
              </span>
            )}
          </div>
        </div>
        {showRewardsRow && (
          <div className="flex items-center gap-2">
            {isPointsLoading && <Skeleton className="h-[22px] w-10 rounded" />}

            {hasPointsBadge &&
              (props.action === 'lp-deposit' ? (
                <PointsBadge
                  multiplier={points?.multiplier}
                  action="lp-deposit"
                  poolName={props.poolName}
                  partnerPoints={partnerPoints}
                  extraIncentives={extraIncentives}
                  additionalAPY={additionalAPY}
                  baseAPY={baseAPYDisplay}
                  totalAPY={totalAPYDisplay}
                />
              ) : (
                <PointsBadge
                  multiplier={points?.multiplier}
                  action={props.action}
                  asset={props.asset}
                  partnerPoints={partnerPoints}
                  extraIncentives={extraIncentives}
                  additionalAPY={additionalAPY}
                  baseAPY={baseAPYDisplay}
                  totalAPY={totalAPYDisplay}
                />
              ))}

            {isPointsError && <Warn>There was an error loading the points data. Please try again later.</Warn>}
          </div>
        )}
      </div>
    </div>
  )
}
